import React, { useState, useEffect } from 'react';
import { message, Popconfirm, Tooltip } from 'antd';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import { TrashIcon, CheckIcon, XIcon, InformationCircleIcon } from '@heroicons/react/outline';
import { colors, spacing, typography } from '../design-system/tokens';
import { projectsService, type Project } from '../services/projectsService';
import { Modal } from '../design-system/components/feedback/Modal';


interface ProjectOverlayProps {
  isOpen: boolean;
  project: Project | null;
  onClose: () => void;
  onSuccess: (project: Project) => void;
  onDelete?: (projectId: string) => void;
  isEmptyState?: boolean; // When true, shows as empty state without close button
}

// Global styles to override modal defaults for project modal
const ProjectModalStyles = createGlobalStyle`
  .project-modal {
    background: ${colors.palette.gray[100]} !important;
    top: 2% !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    height: 96% !important;
    max-height: 96% !important;
    
    /* Remove default modal body padding */
    & > div:last-child {
      padding: 0 !important;
    }
  }
`;

// Styled Components matching CreatorModal pattern
const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const TopPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing[2]} ${spacing[4]};
  background: ${colors.palette.gray[25]};
  border-bottom: 1px solid rgba(0, 0, 0, 0.02);
`;

const TitleSection = styled.div`
  flex: 1;
  max-width: 300px;
  
  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.text.primary};
  }
`;

const ContentArea = styled.div`
  flex: 1;
  background: ${colors.palette.gray[100]};
  padding: ${spacing[2]} ${spacing[8]};
  display: flex;
  justify-content: center;
  overflow-y: auto;
`;

const ContentSheet = styled.div`
  width: 100%;
  max-width: 600px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-height: 500px;
  display: flex;
  flex-direction: column;
  padding: ${spacing[8]};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${spacing[1]};
  align-items: center;
`;

const SaveButton = styled.button`
  background: transparent;
  color: ${colors.text.primary};
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  line-height: 1;
  
  &:hover:not(:disabled) {
    background: ${colors.background.hover};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: ${colors.text.secondary};
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  line-height: 1;
  
  &:hover:not(:disabled) {
    background: ${colors.background.hover};
    color: ${colors.text.primary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[8]};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  margin-bottom: ${spacing[4]};
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding-bottom: ${spacing[2]};
  border-bottom: 1px solid ${colors.border.secondary};
`;

const SimpleForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]};
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

const Label = styled.label`
  font-size: ${typography.fontSizes.sm};
  font-weight: ${typography.fontWeights.medium};
  color: ${colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${colors.border.secondary};
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;
  background: white;
  font-family: ${typography.fontFamilies.sans};
  
  &:focus {
    outline: none;
    border-color: ${colors.palette.indigo[400]};
    box-shadow: 0 0 0 3px ${colors.palette.indigo[100]};
  }
  
  &:hover {
    border-color: ${colors.border.primary};
  }
  
  &::placeholder {
    color: ${colors.text.tertiary};
  }
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${colors.border.secondary};
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;
  background: white;
  font-family: ${typography.fontFamilies.sans};
  resize: vertical;
  min-height: 120px;
  line-height: 1.5;
  
  &:focus {
    outline: none;
    border-color: ${colors.palette.indigo[400]};
    box-shadow: 0 0 0 3px ${colors.palette.indigo[100]};
  }
  
  &:hover {
    border-color: ${colors.border.primary};
  }
  
  &::placeholder {
    color: ${colors.text.tertiary};
  }
`;

const Description = styled.p`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin: 4px 0 0 0;
  line-height: 1.4;
`;

const HelpBox = styled.div`
  background: ${colors.palette.indigo[50]};
  border: 1px solid ${colors.palette.indigo[100]};
  border-radius: 6px;
  padding: ${spacing[3]};
  margin-top: ${spacing[2]};
`;

const HelpTitle = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${colors.text.primary};
  margin-bottom: ${spacing[1]};
`;

const HelpText = styled.p`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin: 0 0 ${spacing[2]} 0;
  line-height: 1.4;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const HelpList = styled.ul`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin: ${spacing[1]} 0 0 0;
  padding-left: 20px;
  line-height: 1.5;
  
  li {
    margin-bottom: 4px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const InfoIcon = styled(InformationCircleIcon)`
  width: 14px;
  height: 14px;
  color: ${colors.text.tertiary};
  opacity: 0.6;
  cursor: help;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    opacity: 1;
  }
`;

const DeleteSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: ${spacing[6]};
  padding-top: ${spacing[4]};
  border-top: 1px solid ${colors.border.secondary};
`;

const DeleteTitle = styled.h3`
  font-size: 14px;
  font-weight: 500;
  color: #dc2626;
  margin: 0;
`;

const DeleteDescription = styled.p`
  font-size: 13px;
  color: ${colors.text.secondary};
  margin: 0;
  line-height: 1.4;
`;

const DeleteButtonContainer = styled.div`
  margin-top: ${spacing[2]};
`;

const DeleteButton = styled.button`
  background: transparent;
  color: #dc2626;
  border: 1px solid rgba(220, 38, 38, 0.2);
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  
  &:hover:not(:disabled) {
    background: rgba(220, 38, 38, 0.05);
    border-color: rgba(220, 38, 38, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

export const ProjectOverlay: React.FC<ProjectOverlayProps> = ({
  isOpen,
  project,
  onClose,
  onSuccess,
  onDelete,
  isEmptyState = false
}) => {
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Initialize values when project changes
  useEffect(() => {
    if (project && isOpen) {
      console.log('[ProjectOverlay] Setting values:', {
        name: project.name,
        description: project.description
      });
      setName(project.name);
      setDescription(project.description || '');
    } else if (isOpen && !project) {
      // Clear form for new project
      setName('');
      setDescription('');
    }
  }, [project, isOpen]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      message.error('Project name is required');
      return;
    }
    
    setLoading(true);
    try {
      if (project) {
        // Update existing project
        console.log('[ProjectOverlay] Starting update for project:', project.project_uuid);
        const updateData: any = {
          name: name.trim(),
          description: description.trim()
        };
        
        const updatedProject = await projectsService.updateProject(project.project_uuid, updateData);
        
        console.log('[ProjectOverlay] Update successful');
        message.success('Project updated successfully!');
        onSuccess(updatedProject);
      } else {
        // Create new project
        console.log('[ProjectOverlay] Creating new project');
        const newProject = await projectsService.createProject({
          name: name.trim(),
          description: description.trim() || undefined
        });
        
        console.log('[ProjectOverlay] Create successful');
        message.success('Project created successfully!');
        onSuccess(newProject);
      }
      
      onClose();
    } catch (error) {
      console.error('[ProjectOverlay] Error:', error);
      message.error(`Failed to ${project ? 'update' : 'create'} project`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!project || !onDelete) return;
    
    console.log('[ProjectOverlay] Starting delete for project:', project.project_uuid);
    
    setDeleteLoading(true);
    try {
      await projectsService.deleteProject(project.project_uuid);
      
      console.log('[ProjectOverlay] Delete successful');
      message.success('Project deleted successfully!');
      
      onDelete(project.project_uuid);
      onClose();
    } catch (error) {
      console.error('[ProjectOverlay] Error deleting project:', error);
      message.error('Failed to delete project');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancel = () => {
    setName('');
    setDescription('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <ProjectModalStyles />
      <Modal
        isOpen={isOpen}
        onClose={!isEmptyState ? handleCancel : () => {}}
        size="full"
        height="full"
        showCloseButton={false}
        className="project-modal"
      >
        <ModalContent>
          <TopPanel>
            <TitleSection>
              <h3>
                {isEmptyState ? 'Welcome to your project' : 
                 project ? 'Edit Project' : 'Create Project'}
              </h3>
            </TitleSection>

            <ActionButtons>
              {!isEmptyState && (
                <SecondaryButton
                  onClick={handleCancel}
                  disabled={loading || deleteLoading}
                >
                  <XIcon />
                  Cancel
                </SecondaryButton>
              )}
              <SaveButton
                onClick={handleSubmit}
                disabled={loading || deleteLoading || !name.trim()}
              >
                {loading ? (
                  <>Saving...</>
                ) : (
                  <>
                    <CheckIcon />
                    {project ? 'Save Changes' : 'Create Project'}
                  </>
                )}
              </SaveButton>
            </ActionButtons>
          </TopPanel>

          <ContentArea>
            <ContentSheet>

                {/* Project Details Section */}
                <Section>
                <SectionHeader>
                  <SectionTitle>
                    Project Details
                  </SectionTitle>
                </SectionHeader>

                <SimpleForm>
                  {/* Project Name */}
                  <FormField>
                    <Label htmlFor="project-name">
                      Project Name
                    </Label>
                    <StyledInput 
                      id="project-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g., Summer Campaign 2024"
                      autoFocus
                      maxLength={100}
                    />
                  </FormField>
                  
                  {/* Project Description */}
                  <FormField>
                    <Label htmlFor="project-context">
                      Project Description
                      <Tooltip 
                        title={
                          <div style={{ fontSize: '12px' }}>
                            <strong>Example:</strong><br />
                            "Instagram sponsored content campaign for GlowSkin promoting their new organic face serum line to beauty and wellness creators"
                          </div>
                        }
                        placement="right"
                      >
                        <InfoIcon />
                      </Tooltip>
                    </Label>
                    <StyledTextArea 
                      id="project-context"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Describe the brand, campaign type, platform, and product/service... (optional)"
                      maxLength={500}
                    />
                    <Description>
                      {description.trim().length > 0 ? `${description.trim().length}/500 characters` : 'Optional - helps the AI agent match and track email conversations'}
                    </Description>
                    
                    {description.trim().length > 0 && (
                      <HelpBox>
                        <HelpTitle>What to include:</HelpTitle>
                        <HelpList>
                          <li>Brand or company name</li>
                          <li>Campaign type (sponsored content, ambassadorship, affiliate, gifting, etc.)</li>
                          <li>Target platforms (Instagram, TikTok, YouTube)</li>
                          <li>Describe product/service promoted</li>
                        </HelpList>
                      </HelpBox>
                    )}
                  </FormField>
                </SimpleForm>
              </Section>

              {/* Delete Section */}
              {project && onDelete && (
                <DeleteSection>
                  <DeleteTitle>Delete Project</DeleteTitle>
                  <DeleteDescription>
                    Permanently delete this project and all its data.
                  </DeleteDescription>
                  
                  <DeleteButtonContainer>
                    <Popconfirm
                      title="Delete Project"
                      description={`Are you sure you want to delete "${project.name}"? This action cannot be undone.`}
                      onConfirm={handleDelete}
                      okText="Delete"
                      cancelText="Cancel"
                      okType="danger"
                      placement="topRight"
                    >
                      <DeleteButton 
                        disabled={loading || deleteLoading}
                      >
                        {deleteLoading ? (
                          <>Deleting...</>
                        ) : (
                          <>
                            <TrashIcon />
                            Delete Project
                          </>
                        )}
                      </DeleteButton>
                    </Popconfirm>
                  </DeleteButtonContainer>
                </DeleteSection>
              )}
            </ContentSheet>
          </ContentArea>
        </ModalContent>
      </Modal>
    </>
  );
};
